<template>
  <div class="wds-category-selector">
    <div class="selector-header">
      <img v-if="imageUrl" :src="imageUrl" class="selector-preview-image" alt="Skin preview" />
      <h2>{{ title }}</h2>
    </div>

    <div class="selector-form">
      <!-- Game Selection -->
      <div class="form-group">
        <label>Game</label>
        <select v-model="selectedGame" @change="onGameChange" class="dropdown" :disabled="isLoading">
          <option :value="null">{{ isLoading ? 'Loading games...' : 'Select a game...' }}</option>
          <option v-for="game in games" :key="game.IdGame" :value="game">
            {{ game.GameName }}
          </option>
        </select>
      </div>

      <!-- Skin Type Selection (after game selection) -->
      <div v-if="selectedGame" class="form-group">
        <label>This skin is a:</label>
        <div class="radio-group radio-horizontal">
          <label class="radio-item">
            <input type="radio" v-model="skinType" value="model" />
            Model
          </label>
          <label class="radio-item">
            <input type="radio" v-model="skinType" value="terrain" />
            Terrain
          </label>
          <label class="radio-item">
            <input type="radio" v-model="skinType" value="token" />
            Token
          </label>
        </div>
      </div>

      <!-- Terrain Selection (if Terrain is selected) -->
      <div v-if="selectedGame && skinType === 'terrain'" class="form-group">
        <label>Terrain</label>
        <div v-if="terrainLoading" class="hierarchy-loading">
          <div class="hierarchy-spinner"></div>
          <span>Loading terrains...</span>
        </div>
        <select v-if="!terrainLoading" v-model="selectedTerrain" class="dropdown">
          <option :value="null">Select terrain...</option>
          <option v-for="terrain in terrains" :key="terrain.Id" :value="terrain">
            {{ terrain.Name }}
          </option>
        </select>
      </div>

      <!-- Token Selection (if Token is selected) -->
      <div v-if="selectedGame && skinType === 'token'" class="form-group">
        <label>Token</label>
        <div v-if="tokenLoading" class="hierarchy-loading">
          <div class="hierarchy-spinner"></div>
          <span>Loading tokens...</span>
        </div>
        <select v-if="!tokenLoading" v-model="selectedToken" class="dropdown">
          <option :value="null">Select token...</option>
          <option v-for="token in tokens" :key="token.Id" :value="token">
            {{ token.Name }}
          </option>
        </select>
      </div>

      <!-- Hierarchy Loading Spinner -->
      <div v-if="skinType === 'model' && hierarchyLoading" class="hierarchy-loading">
        <div class="hierarchy-spinner"></div>
        <span>Loading game data...</span>
      </div>

      <!-- Hierarchy-driven dropdowns (only for Model type) -->
      <template v-if="skinType === 'model' && !hierarchyLoading && hierarchyData">

        <div v-for="(level, index) in hierarchyLevels" :key="index" class="form-group">
          <label>{{ getLevelLabel(index) }}</label>
          <select
            :value="hierarchyPath[index + 1] || ''"
            @change="onHierarchySelect(index + 1, $event.target.value)"
            class="dropdown"
          >
            <option value="">Select {{ getLevelLabel(index).toLowerCase() }}...</option>
            <option v-for="option in level.options" :key="option" :value="option">
              {{ option }}
            </option>
          </select>
        </div>

        <!-- Units loading spinner (shown while background fetch runs and user reached a leaf) -->
        <div v-if="currentLeaf && unitsLoading" class="hierarchy-loading">
          <div class="hierarchy-spinner"></div>
          <span>Loading units...</span>
        </div>

        <!-- Final unit selection at leaf (displays unit names, sends profile IDs) -->
        <div v-if="currentLeaf && !unitsLoading" class="form-group">
          <label>Unit</label>
          <select :value="selectedUnitId || ''" @change="onUnitSelect($event.target.value)" class="dropdown">
            <option value="">Select unit...</option>
            <option v-for="id in currentLeaf" :key="id" :value="id">
              {{ resolveUnitName(id) }}
            </option>
          </select>
        </div>

        <!-- Transform options (mutually exclusive — change the unit's identity) -->
        <div v-if="transformOptions.length > 0" class="form-group">
          <label>Mount / Formation</label>
          <div class="options-list options-list--indented">
            <label
              v-for="opt in transformOptions"
              :key="opt.id"
              class="option-checkbox-item"
            >
              <input type="radio" v-model="selectedTransformOptionId" :value="opt.id" />
              {{ opt.name }}
            </label>
          </div>
        </div>

        <!-- Regular options (additive modifiers — checkboxes) -->
        <div v-if="regularOptions.length > 0" class="form-group">
          <label>Options</label>
          <div class="options-list">
            <label
              v-for="opt in regularOptions"
              :key="opt.id"
              class="option-checkbox-item"
            >
              <input type="checkbox" :value="opt.id" v-model="selectedOptionIds" />
              {{ opt.name }}
            </label>
          </div>
        </div>

        <!-- Optional skin name input (used by Catalogue after unit is selected) -->
        <div v-if="showUnitNameInput && canEnterUnitName" class="form-group">
          <label>{{ unitNameLabel }}</label>
          <input
            v-model="unitName"
            type="text"
            class="text-input"
            :placeholder="unitNamePlaceholder"
            @input="onUnitNameChange"
          />
        </div>

      </template>
    </div>

  </div>
</template>

<script>
export default {
  name: 'WDSCategorySelector',
  props: {
    title: {
      type: String,
      default: 'Select Category'
    },
    subtitle: {
      type: String,
      default: ''
    },
    showUnitNameInput: {
      type: Boolean,
      default: true
    },
    unitNameLabel: {
      type: String,
      default: 'Unit Name'
    },
    unitNamePlaceholder: {
      type: String,
      default: 'Enter unit name...'
    },
    serverUrl: {
      type: String,
      default: 'https://server.warhall.eu'
    },
    imageUrl: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      games: [],
      selectedGame: null,
      skinType: 'model', // 'model', 'terrain', or 'token'
      unitName: '',
      isLoading: false,
      hierarchyLoading: false,
      hierarchyData: null,
      hierarchyPath: [],
      selectedUnitId: null,
      unitNameMap: {},
      unitToProfilesMap: {},
      unitsLoading: false,
      baseUrl: null,
      unitModifiersMap: {},
      availableOptions: [],
      selectedOptionIds: [],
      selectedTransformOptionId: null,
      terrains: [],
      tokens: [],
      selectedTerrain: null,
      selectedToken: null,
      terrainLoading: false,
      tokenLoading: false
    };
  },
  computed: {
    hierarchyLevels() {
      if (!this.hierarchyData) return [];
      const levels = [];
      let current = this.hierarchyData;
      for (let i = 0; ; i++) {
        if (Array.isArray(current)) break;
        const options = Object.keys(current);
        // Skip the top-level (Factions/Cards) — it is pre-selected automatically
        if (i > 0) {
          levels.push({ options });
        }
        const selectedKey = this.hierarchyPath[i];
        if (!selectedKey || current[selectedKey] === undefined) break;
        current = current[selectedKey];
      }
      return levels;
    },
    currentLeaf() {
      if (!this.hierarchyData || this.hierarchyPath.length === 0) return null;
      let current = this.hierarchyData;
      for (const key of this.hierarchyPath) {
        if (Array.isArray(current)) return null;
        if (current[key] === undefined) return null;
        current = current[key];
      }
      return Array.isArray(current) ? current : null;
    },
    transformOptions() {
      return this.availableOptions.filter(o => o.newUnitId);
    },
    regularOptions() {
      return this.availableOptions.filter(o => !o.newUnitId);
    },
    selectedTransformOption() {
      return this.transformOptions.find(o => o.id === this.selectedTransformOptionId) || null;
    },
    resolvedUnitId() {
      return this.selectedTransformOption ? this.selectedTransformOption.newUnitId : this.selectedUnitId;
    },
    resolvedUnitName() {
      if (!this.resolvedUnitId) return null;
      const baseName = this.unitNameMap[this.selectedUnitId] || this.selectedUnitId;
      if (this.selectedTransformOption) return `${baseName} (${this.selectedTransformOption.name})`;
      return baseName;
    },
    canEnterUnitName() {
      return this.selectedUnitId !== null;
    },
    isComplete() {
      if (this.skinType === 'terrain') {
        return this.selectedTerrain !== null;
      } else if (this.skinType === 'token') {
        return this.selectedToken !== null;
      } else {
        // model type
        return this.canEnterUnitName;
      }
    },
    selectionData() {
      if (this.skinType === 'terrain') {
        return {
          game: this.selectedGame,
          skinType: 'terrain',
          selectedTerrain: this.selectedTerrain,
          isComplete: this.isComplete
        };
      } else if (this.skinType === 'token') {
        return {
          game: this.selectedGame,
          skinType: 'token',
          selectedToken: this.selectedToken,
          isComplete: this.isComplete
        };
      } else {
        // Model type
        return {
          game: this.selectedGame,
          skinType: 'model',
          hierarchyPath: this.hierarchyPath,
          selectedUnitId: this.resolvedUnitId,
          selectedUnitName: this.resolvedUnitName,
          unitName: this.unitName,
          canEnterUnitName: this.canEnterUnitName,
          isComplete: this.isComplete,
          selectedOptionIds: this.selectedOptionIds,
          selectedOptions: this.regularOptions.filter(o => this.selectedOptionIds.includes(o.id))
        };
      }
    }
  },
  watch: {
    selectionData: {
      handler(newVal) {
        this.$emit('selection-changed', newVal);
      },
      deep: true
    },
    selectedTerrain(newVal) {
      if (this.skinType === 'terrain') {
        this.$emit('selection-changed', this.selectionData);
      }
    },
    selectedToken(newVal) {
      if (this.skinType === 'token') {
        this.$emit('selection-changed', this.selectionData);
      }
    }
  },
  async mounted() {
    await this.loadGames();
    await this.restoreFromCache();
  },
  methods: {
    getCacheKey() {
      return 'wds_category_selector_cache';
    },

    saveToCache() {
      const cache = {
        games: this.games,
        selectedGame: this.selectedGame,
        timestamp: Date.now()
      };
      try {
        localStorage.setItem(this.getCacheKey(), JSON.stringify(cache));
      } catch (err) {
        console.error('Failed to save cache:', err);
      }
    },

    async restoreFromCache() {
      try {
        const cached = localStorage.getItem(this.getCacheKey());
        if (!cached) return;
        const cache = JSON.parse(cached);
        const cacheAge = Date.now() - (cache.timestamp || 0);
        if (cacheAge > 24 * 60 * 60 * 1000) {
          localStorage.removeItem(this.getCacheKey());
          return;
        }
        // Do not restore selectedGame — user must pick a game to trigger hierarchy download
      } catch (err) {
        console.error('Failed to restore from cache:', err);
        localStorage.removeItem(this.getCacheKey());
      }
    },

    clearCache() {
      try {
        localStorage.removeItem(this.getCacheKey());
      } catch (err) {
        console.error('Failed to clear cache:', err);
      }
    },

    async loadGames() {
      this.isLoading = true;
      try {
        // Check cache first
        const cached = localStorage.getItem(this.getCacheKey());
        if (cached) {
          const cache = JSON.parse(cached);
          if (cache.games && cache.games.length > 0) {
            this.games = cache.games;
            this.isLoading = false;
            return;
          }
        }

        const response = await fetch(`${this.serverUrl}/GetGamesWeb/`, {
          credentials: 'include'
        });

        const data = await response.json();
        if (!data || !data.success) {
          throw new Error('Failed to load games');
        }

        // Filter only available games (GameAvailability = 1)
        this.games = data.games.filter(game => game.GameAvailability === 1);
        
        // Save to cache
        this.saveToCache();
      } catch (err) {
        console.error('Error loading games:', err);
        alert('Failed to load games from server.');
      } finally {
        this.isLoading = false;
      }
    },

    async onGameChange() {
      this.hierarchyData = null;
      this.hierarchyPath = [];
      this.selectedUnitId = null;
      this.unitNameMap = {};
      this.unitToProfilesMap = {};
      this.unitName = '';
      this.terrains = [];
      this.tokens = [];
      this.selectedTerrain = null;
      this.selectedToken = null;
      this.skinType = 'model'; // Reset to default
      this.availableOptions = [];
      this.selectedOptionIds = [];
      this.selectedTransformOptionId = null;
      this.unitModifiersMap = {};

      if (!this.selectedGame) {
        this.saveToCache();
        return;
      }

      // Load hierarchy for models and also load terrains/tokens for other types
      await this.loadHierarchy(this.selectedGame.IdGame);
      await this.loadTerrains(this.selectedGame.IdGame);
      await this.loadTokens(this.selectedGame.IdGame);
      this.saveToCache();
    },

    async loadHierarchy(gameId) {
      this.hierarchyLoading = true;
      this.hierarchyData = null;
      try {
        const ltsResponse = await fetch(`${this.serverUrl}/GetGameLTSPath/${gameId}`, {
          credentials: 'include'
        });
        const ltsData = await ltsResponse.json();

        if (ltsData.error !== 0) {
          throw new Error(ltsData.result || 'No LTS version found for this game');
        }

        // Derive base URL by stripping manifest.json
        const baseUrl = ltsData.result.replace(/manifest\.json$/, '');
        const hierarchyUrl = baseUrl + 'hierarchy.json';

        const hierarchyResponse = await fetch(hierarchyUrl);
        if (!hierarchyResponse.ok) {
          throw new Error(`Failed to fetch hierarchy: ${hierarchyResponse.status}`);
        }
        this.hierarchyData = await hierarchyResponse.json();

        // Always start inside Factions — skip the top-level Factions/Cards choice
        if (this.hierarchyData && this.hierarchyData['Factions']) {
          this.hierarchyPath = ['Factions'];
        }

        // Save base URL and start fetching unit names + modifiers in the background (non-blocking)
        this.baseUrl = baseUrl;
        this.buildUnitNameMap(baseUrl);
      } catch (err) {
        console.error('Error loading hierarchy:', err);
        alert('Failed to load game data: ' + err.message);
      } finally {
        this.hierarchyLoading = false;
        this.$nextTick(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }));
      }
    },

    async buildUnitNameMap(baseUrl) {
      this.unitsLoading = true;
      try {
        const manifestUrl = baseUrl + 'manifest.json';
        console.log('Downloading manifest from:', manifestUrl);
        const manifestResponse = await fetch(manifestUrl);
        if (!manifestResponse.ok) throw new Error('Failed to fetch manifest');
        const manifest = await manifestResponse.json();

        const files = manifest.files || [];

        // Find all army units.json paths (pattern: ArmyName/units.json)
        const unitFiles = files.filter(f => f.endsWith('/units.json'));

        // Find army folders that have modifiedprofile.json
        const modifiedProfileFiles = files.filter(f => f.endsWith('/modifiedprofile.json'));
        const armyDirsWithModifiers = new Set(
          modifiedProfileFiles.map(f => f.replace(/\/modifiedprofile\.json$/, ''))
        );

        // Fetch units and map profile IDs to unit names
        const unitResults = await Promise.all(
          unitFiles.map(file =>
            fetch(baseUrl + file)
              .then(r => r.ok ? r.json() : [])
              .catch(() => [])
          )
        );

        const map = {};
        const profilesMap = {};
        for (const units of unitResults) {
          if (Array.isArray(units)) {
            for (const unit of units) {
              // Map unit ID to unit name
              if (unit.Id && unit.Name) {
                map[unit.Id] = unit.Name;
              }
              // Map unit ID to its profile IDs
              if (unit.Id && unit.BasicProfileIds && Array.isArray(unit.BasicProfileIds)) {
                profilesMap[unit.Id] = unit.BasicProfileIds;
              }
              // Also map each profile ID in BasicProfileIds to the unit name
              if (unit.BasicProfileIds && Array.isArray(unit.BasicProfileIds) && unit.Name) {
                for (const profileId of unit.BasicProfileIds) {
                  map[profileId] = unit.Name;
                }
              }
            }
          }
        }
        this.unitNameMap = map;
        this.unitToProfilesMap = profilesMap;

        // Build modifiers map: { profileId: [{ id, name }] }
        const modifiersMap = {};
        await Promise.all(
          [...armyDirsWithModifiers].map(async armyDir => {
            try {
              const [modifiedProfiles, optionsArr] = await Promise.all([
                fetch(baseUrl + armyDir + '/modifiedprofile.json').then(r => r.ok ? r.json() : []).catch(() => []),
                fetch(baseUrl + armyDir + '/options.json').then(r => r.ok ? r.json() : []).catch(() => [])
              ]);

              if (!Array.isArray(modifiedProfiles) || !Array.isArray(optionsArr)) return;

              const optionsById = {};
              for (const opt of optionsArr) {
                if (opt.Id) optionsById[opt.Id] = opt;
              }

              for (const entry of modifiedProfiles) {
                const { OptionId, ProfileId } = entry;
                if (!OptionId || !ProfileId) continue;
                const opt = optionsById[OptionId];
                if (!opt) continue;
                if (!modifiersMap[ProfileId]) modifiersMap[ProfileId] = [];
                if (!modifiersMap[ProfileId].some(o => o.id === OptionId)) {
                  modifiersMap[ProfileId].push({ id: opt.Id, name: opt.Name, newUnitId: entry.NewProfileId || null });
                }
              }
            } catch (err) {
              console.error(`Failed to build modifiers for ${armyDir}:`, err);
            }
          })
        );
        this.unitModifiersMap = modifiersMap;
      } catch (err) {
        console.error('Failed to build unit name map:', err);
      } finally {
        this.unitsLoading = false;
      }
    },

    async loadTerrains(gameId) {
      this.terrainLoading = true;
      try {
        // Get the LTS path for this game
        const ltsResponse = await fetch(`${this.serverUrl}/GetGameLTSPath/${gameId}`, {
          credentials: 'include'
        });
        const ltsData = await ltsResponse.json();

        if (ltsData.error !== 0) {
          console.error('Failed to get game LTS path');
          return;
        }

        // Derive base URL by stripping manifest.json
        const baseUrl = ltsData.result.replace(/manifest\.json$/, '');
        const terrainsUrl = baseUrl + 'terrains.json';

        const response = await fetch(terrainsUrl);
        if (!response.ok) {
          console.warn('Terrains file not found');
          this.terrains = [];
          return;
        }

        const data = await response.json();
        this.terrains = Array.isArray(data) ? data : [];
      } catch (err) {
        console.error('Failed to load terrains:', err);
        this.terrains = [];
      } finally {
        this.terrainLoading = false;
      }
    },

    async loadTokens(gameId) {
      this.tokenLoading = true;
      try {
        // Get the LTS path for this game
        const ltsResponse = await fetch(`${this.serverUrl}/GetGameLTSPath/${gameId}`, {
          credentials: 'include'
        });
        const ltsData = await ltsResponse.json();

        if (ltsData.error !== 0) {
          console.error('Failed to get game LTS path');
          return;
        }

        // Derive base URL by stripping manifest.json
        const baseUrl = ltsData.result.replace(/manifest\.json$/, '');
        const tokensUrl = baseUrl + 'tokens.json';

        const response = await fetch(tokensUrl);
        if (!response.ok) {
          console.warn('Tokens file not found');
          this.tokens = [];
          return;
        }

        const data = await response.json();
        this.tokens = Array.isArray(data) ? data : [];
      } catch (err) {
        console.error('Failed to load tokens:', err);
        this.tokens = [];
      } finally {
        this.tokenLoading = false;
      }
    },

    onHierarchySelect(levelIndex, key) {
      if (!key) {
        this.hierarchyPath = this.hierarchyPath.slice(0, levelIndex);
      } else {
        this.hierarchyPath = [...this.hierarchyPath.slice(0, levelIndex), key];
      }
      this.selectedUnitId = null;
      this.unitName = '';
      this.availableOptions = [];
      this.selectedOptionIds = [];
      this.selectedTransformOptionId = null;
      this.$nextTick(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }));
    },

    onUnitSelect(id) {
      this.selectedUnitId = id || null;
      this.unitName = '';
      this.selectedOptionIds = [];
      this.selectedTransformOptionId = null;
      
      // Look up available options by ProfileId (BasicProfileIds), not by unit ID
      let options = [];
      if (id && this.unitToProfilesMap && this.unitToProfilesMap[id]) {
        const profileIds = this.unitToProfilesMap[id];
        for (const profileId of profileIds) {
          if (this.unitModifiersMap[profileId]) {
            options = options.concat(this.unitModifiersMap[profileId]);
          }
        }
      }
      this.availableOptions = options;
      this.$nextTick(() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' }));
    },

    resolveUnitName(id) {
      return this.unitNameMap[id] || id;
    },

    getLevelLabel(index) {
      if (index === 0) return 'Faction';
      return 'Subcategory';
    },

    onUnitNameChange() {
      this.saveToCache();
      // Emit change event
      this.$emit('selection-changed', this.selectionData);
    },

    reset() {
      this.selectedGame = null;
      this.skinType = 'model';
      this.hierarchyData = null;
      this.hierarchyPath = [];
      this.selectedUnitId = null;
      this.unitNameMap = {};
      this.unitToProfilesMap = {};
      this.unitName = '';
      this.baseUrl = null;
      this.unitModifiersMap = {};
      this.availableOptions = [];
      this.selectedOptionIds = [];
      this.selectedTransformOptionId = null;
      this.terrains = [];
      this.tokens = [];
      this.selectedTerrain = null;
      this.selectedToken = null;
      this.clearCache();
    }
  }
};
</script>

<style scoped>
.wds-category-selector {
  display: flex;
  flex-direction: column;
  gap: 24px;
  background: #1a2332;
  padding: 24px;
  border-radius: 12px;
}

.selector-header {
  text-align: center;
}

.selector-preview-image {
  display: block;
  margin: 0 auto 12px auto;
  width: 256px;
  height: 256px;
  border-radius: 8px;
  object-fit: contain;
}

.selector-header h2 {
  margin: 0 0 8px 0;
  color: #ffffff;
  font-size: 1.5em;
}

.subtitle {
  margin: 0;
  color: #b8c5d6;
  font-size: 0.95em;
}

.selector-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 600;
  color: #e8eef5;
  font-size: 0.95em;
}

.dropdown,
.text-input {
  padding: 12px 16px;
  border: 2px solid #2c3e50;
  border-radius: 10px;
  font-size: 1em;
  transition: all 0.3s ease;
  background: #1a2332;
  color: #ffffff;
}

.dropdown:focus,
.text-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
  background: #243447;
}

.dropdown:disabled {
  background: #0f1621;
  cursor: not-allowed;
  color: #6c7a8d;
}

.dropdown option {
  background: #1a2332;
  color: #ffffff;
}

.selection-summary {
  background: #243447;
  border-left: 4px solid #667eea;
  padding: 16px;
  border-radius: 8px;
  font-size: 0.9em;
}

.selection-summary strong {
  display: block;
  margin-bottom: 8px;
  color: #e8eef5;
}

.path-display {
  color: #8b9fea;
  font-weight: 500;
  word-break: break-word;
}

.hierarchy-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #b8c5d6;
  font-size: 0.95em;
  padding: 8px 0;
}

.hierarchy-spinner {
  width: 20px;
  height: 20px;
  border: 3px solid #2c3e50;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Modifier checkboxes */
.options-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.options-list--indented {
  padding-left: 16px;
}

.option-checkbox-item {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #e8eef5;
  font-size: 0.95em;
  cursor: pointer;
  user-select: none;
  background: #243447;
  border: 1px solid #2c3e50;
  border-radius: 8px;
  padding: 10px 14px;
  transition: border-color 0.2s;
}

.option-checkbox-item:hover {
  border-color: #667eea;
}

.option-checkbox-item input[type="checkbox"],
.option-checkbox-item input[type="radio"] {
  accent-color: #667eea;
  width: 16px;
  height: 16px;
  min-width: 16px;
  cursor: pointer;
  flex-shrink: 0;
  align-self: center;
}

/* Radio Group Styles */
.radio-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.radio-group.radio-horizontal {
  flex-direction: row;
  gap: 24px;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;
}

.radio-item {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: #e8eef5;
  font-size: 0.95em;
}

.radio-item input[type="radio"] {
  accent-color: #667eea;
  width: 16px;
  height: 16px;
  min-width: 16px;
  cursor: pointer;
  flex-shrink: 0;
}

.radio-item:hover {
  color: #ffffff;
}

/* Responsive */
@media (max-width: 768px) {
  .radio-group.radio-horizontal {
    flex-direction: column;
    gap: 12px;
  }
}
@media (max-width: 768px) {
  .selector-form {
    gap: 16px;
  }
}
</style>
